import DOMPurify, { type Config, type WindowLike } from "dompurify";
import { JSDOM } from "jsdom";
import { serverOnly$ } from "vite-env-only/macros";

// DOMPurify sanitizes HTML using a DOM. In the browser it uses the global
// `window`, but during server-side rendering there is no DOM, so we back it
// with a jsdom `window` created once and reused for every call.
const serverPurifier = serverOnly$(DOMPurify(new JSDOM("").window as unknown as WindowLike));

const purifier = serverPurifier ?? DOMPurify;

// StringHelper::DEFAULT_HTML_ELEMENT_ALLOWLIST plus the job post model's
// `element_allowlist`
const ALLOWED_TAGS = [
  "#text", // Required because KEEP_CONTENT is false
  "a",
  "b",
  "i",
  "strong",
  "del",
  "u",
  "em",
  "p",
  "param",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "br",
  "hr",
  "ul",
  "ol",
  "li",
  "img",
  "span",
  "div",
  "s",
  "sub",
  "sup",
  "pre",
  "blockquote",
  "table",
  "tbody",
  "th",
  "tr",
  "td",
  "thead",
  "tfoot",
  "section",
  "article",
  "aside",
  "mark",
  "time",
  "nav",
  "header",
  "footer",
  "small",
  "code",
  "iframe",
  "video",
  "source",
];

// StringHelper::DEFAULT_HTML_ATTRIBUTE_ALLOWLIST plus the job post model's
// `attribute_allowlist`
const ALLOWED_ATTR = [
  "alt",
  "border",
  "cellpadding",
  "cellspacing",
  "controls",
  "height",
  "href",
  "id",
  "key",
  "lang",
  "modal_path",
  "modal_title",
  "name",
  "poster",
  "src",
  "style",
  "target",
  "type",
  "value",
  "width",
  "class",
];

const CONFIG: Config = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,

  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+\-.]+(?:[^a-z+.\-:]|$))/i,

  // Equivalent to Sanitize's `remove_contents: true`
  KEEP_CONTENT: false,
};

/**
 * Sanitizes an untrusted HTML string. Use this for any value passed to
 * `dangerouslySetInnerHTML` that originates from user data.
 *
 * Works during SSR and in the browser.
 */
export const sanitizeHtml = (html: string | null | undefined): string =>
  purifier.sanitize(html ?? "", CONFIG);
